/**
 * Guardian Home Page
 *
 * Dashboard for guardians/parents showing their linked athletes.
 * Provides quick access to view athlete videos and progress.
 */

import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { listFamilyLinks, listChallenges, type Organization, type FamilyLink, type Challenge } from '@tss/shared';
import {
  Users,
  Video,
  Bell,
  ChevronRight,
  UserPlus,
  Heart,
  Trophy,
  Flame,
  Star,
} from 'lucide-react';
import { AnnouncementsFeed } from '../components/AnnouncementsFeed';
import { GuardianProgressReport } from '../components/GuardianProgressReport';
import { EmptyState, Skeleton, PageHeader } from '../components/UI';

interface GuardianHomePageProps {
  org: Organization | null;
}

export function GuardianHomePage({ org }: GuardianHomePageProps) {
  const navigate = useNavigate();
  const [links, setLinks] = useState<FamilyLink[]>([]);
  const [challenges, setChallenges] = useState<Challenge[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (org) {
      loadData();
    } else {
      setLoading(false);
    }
  }, [org]);

  async function loadData() {
    if (!org) return;
    setLoading(true);
    try {
      const [linksData, challengesData] = await Promise.all([
        listFamilyLinks(org.org_id),
        listChallenges(org.org_id).catch(() => ({ challenges: [] })),
      ]);
      const approvedLinks = linksData.links?.filter(l => l.status === 'APPROVED') || [];
      setLinks(approvedLinks);

      // Filter challenges to only those where linked athletes are participating
      const athleteIds = approvedLinks.map(l => l.athlete_id);
      const relevantChallenges = (challengesData.challenges || []).filter(c =>
        c.participant_ids.some(pid => athleteIds.includes(pid))
      );
      setChallenges(relevantChallenges);
    } catch (err) {
      console.error('Failed to load guardian data:', err);
    } finally {
      setLoading(false);
    }
  }

  // Get athlete challenge stats
  const athleteIds = links.map(l => l.athlete_id);
  const activeChallenges = challenges.filter(c => c.status === 'ACTIVE');
  // Find challenges where the linked athlete actually won (raffle winner)
  const wonChallenges = challenges.filter(c =>
    c.status === 'COMPLETED' &&
    c.raffle_winner_id &&
    athleteIds.includes(c.raffle_winner_id)
  );

  if (loading) {
    return (
      <div style={{ maxWidth: '900px', margin: '0 auto' }}>
        <Skeleton height={36} width="50%" style={{ marginBottom: 8 }} />
        <Skeleton height={20} width="65%" style={{ marginBottom: 32 }} />
        <Skeleton.Card count={2} />
      </div>
    );
  }

  return (
    <div style={{ maxWidth: '900px', margin: '0 auto' }}>
      {/* Welcome Header */}
      <PageHeader
        title="Welcome, Parent"
        description="Stay connected with your athlete's training progress"
        icon={Heart}
        iconColor="var(--accent)"
      />

      {/* Info Banner */}
      <div
        className="card mb-6"
        style={{
          background: 'linear-gradient(135deg, rgba(123, 75, 216, 0.08) 0%, rgba(168, 85, 247, 0.04) 100%)',
          border: '1px solid rgba(123, 75, 216, 0.15)',
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
          <div
            className="icon-container icon-container-md"
            style={{ background: 'rgba(123, 75, 216, 0.15)' }}
          >
            <Users size={22} style={{ color: 'var(--accent)' }} />
          </div>
          <div>
            <h3 style={{ fontWeight: '600', color: 'var(--gray-900)', marginBottom: '4px', fontSize: '15px' }}>
              Guardian Access
            </h3>
            <p style={{ fontSize: '14px', color: 'var(--gray-600)' }}>
              View your athlete's training videos, track their progress, and stay informed about their development.
            </p>
          </div>
        </div>
      </div>

      {/* Announcements Feed */}
      <AnnouncementsFeed trigger="login" maxItems={3} />

      {/* AI Progress Report - only show if we have linked athletes */}
      {!loading && links.length > 0 && (
        <GuardianProgressReport />
      )}

      {/* Challenge Win Celebration */}
      {!loading && wonChallenges.length > 0 && (
        <div
          className="card"
          style={{
            marginBottom: '32px',
            padding: '20px',
            background: 'linear-gradient(135deg, rgba(34, 197, 94, 0.1) 0%, rgba(16, 185, 129, 0.05) 100%)',
            border: '2px solid rgba(34, 197, 94, 0.3)',
          }}
        >
          <div style={{ display: 'flex', alignItems: 'flex-start', gap: '16px' }}>
            <div
              style={{
                width: '56px',
                height: '56px',
                borderRadius: '50%',
                background: 'linear-gradient(135deg, #22c55e 0%, #16a34a 100%)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                flexShrink: 0,
                boxShadow: '0 4px 12px rgba(34, 197, 94, 0.3)',
              }}
            >
              <Star size={28} style={{ color: 'white' }} />
            </div>
            <div style={{ flex: 1 }}>
              <h3 style={{ fontWeight: '700', color: 'var(--success)', fontSize: '18px', marginBottom: '6px' }}>
                Champion Alert!
              </h3>
              <p style={{ fontSize: '15px', color: 'var(--gray-700)', marginBottom: '12px' }}>
                {links.length === 1 ? links[0].athlete_name : 'Your athlete'} won{' '}
                <strong>{wonChallenges.length === 1 ? wonChallenges[0].title : `${wonChallenges.length} challenges`}</strong>!
              </p>
              {wonChallenges[0].prize_description && (
                <p style={{ fontSize: '13px', color: 'var(--gray-500)' }}>
                  Prize: {wonChallenges[0].prize_description}
                </p>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Athlete Challenges Card */}
      {!loading && links.length > 0 && activeChallenges.length > 0 && (
        <div
          className="card"
          style={{
            marginBottom: '32px',
            padding: '20px',
            background: 'linear-gradient(135deg, rgba(245, 158, 11, 0.08) 0%, rgba(251, 191, 36, 0.04) 100%)',
            border: '1px solid rgba(245, 158, 11, 0.2)',
          }}
        >
          <div style={{ display: 'flex', alignItems: 'flex-start', gap: '16px' }}>
            <div
              style={{
                width: '48px',
                height: '48px',
                borderRadius: '50%',
                background: 'rgba(245, 158, 11, 0.15)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                flexShrink: 0,
              }}
            >
              <Trophy size={24} style={{ color: '#f59e0b' }} />
            </div>
            <div style={{ flex: 1 }}>
              <h3 style={{ fontWeight: '600', color: 'var(--gray-900)', fontSize: '16px', marginBottom: '6px' }}>
                <Flame size={16} style={{ display: 'inline', verticalAlign: 'middle', marginRight: '6px', color: '#f59e0b' }} />
                Competition mode is ON!
              </h3>
              <p style={{ fontSize: '14px', color: 'var(--gray-600)', marginBottom: '12px' }}>
                {links.length === 1 ? links[0].athlete_name : 'Your athletes'} {links.length === 1 ? 'is' : 'are'} battling it out in{' '}
                <strong>{activeChallenges.length} challenge{activeChallenges.length !== 1 ? 's' : ''}</strong> right now
              </p>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
                {activeChallenges.slice(0, 3).map(challenge => (
                  <span
                    key={challenge.challenge_id}
                    style={{
                      padding: '6px 12px',
                      background: 'rgba(245, 158, 11, 0.1)',
                      borderRadius: '16px',
                      fontSize: '13px',
                      fontWeight: '500',
                      color: '#b45309',
                    }}
                  >
                    {challenge.title}
                  </span>
                ))}
                {activeChallenges.length > 3 && (
                  <span
                    style={{
                      padding: '6px 12px',
                      background: 'var(--gray-100)',
                      borderRadius: '16px',
                      fontSize: '13px',
                      color: 'var(--gray-600)',
                    }}
                  >
                    +{activeChallenges.length - 3} more
                  </span>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Athletes Section */}
      <div style={{ marginBottom: '32px' }}>
        <div className="flex-between" style={{ marginBottom: '16px' }}>
          <h2 style={{ fontSize: '18px', fontWeight: '600', color: 'var(--gray-900)' }}>
            My Athletes
          </h2>
          <button
            onClick={() => navigate('/family')}
            className="btn-ghost"
            style={{ fontSize: '14px', color: 'var(--accent)', boxShadow: 'none', padding: '8px 12px' }}
          >
            Manage
            <ChevronRight size={16} />
          </button>
        </div>

        {links.length === 0 ? (
          <div className="card">
            <EmptyState
              icon={UserPlus}
              title="No athletes linked yet"
              description="Your athlete's coach will send you an invite to connect. Check your email for the invite link."
            />
          </div>
        ) : (
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(min(100%, 280px), 1fr))', gap: '16px' }}>
            {links.map((link) => (
              <AthleteCard key={link.link_id} link={link} />
            ))}
          </div>
        )}
      </div>

      {/* Quick Actions */}
      {links.length > 0 && (
        <div>
          <h2 style={{ fontSize: '18px', fontWeight: '600', color: 'var(--gray-900)', marginBottom: '16px' }}>
            Quick Actions
          </h2>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '12px' }}>
            <QuickActionCard
              icon={Video}
              title="View Videos"
              description="Watch training videos"
              onClick={() => navigate('/videos')}
            />
            <QuickActionCard
              icon={Bell}
              title="Notifications"
              description="Manage email alerts"
              onClick={() => navigate('/family')}
            />
            <QuickActionCard
              icon={Users}
              title="My Athletes"
              description="Manage linked athletes"
              onClick={() => navigate('/family')}
            />
          </div>
        </div>
      )}
    </div>
  );
}

function AthleteCard({ link }: { link: FamilyLink }) {
  const navigate = useNavigate();

  return (
    <div
      className="card card-interactive"
      style={{ padding: '20px' }}
      onClick={() => navigate('/videos')}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
        <div
          style={{
            width: '52px',
            height: '52px',
            borderRadius: '50%',
            background: 'linear-gradient(135deg, #7C3AED 0%, #a855f7 100%)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: 'white',
            fontSize: '20px',
            fontWeight: '600',
            flexShrink: 0,
          }}
        >
          {link.athlete_name?.charAt(0)?.toUpperCase() || 'A'}
        </div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <h3 className="truncate" style={{ fontWeight: '600', color: 'var(--gray-900)', fontSize: '16px', marginBottom: '4px' }}>
            {link.athlete_name || 'Athlete'}
          </h3>
          <p style={{ fontSize: '13px', color: 'var(--gray-500)', textTransform: 'capitalize' }}>
            {link.relationship.toLowerCase()}
          </p>
        </div>
        <ChevronRight size={20} style={{ color: 'var(--gray-400)', flexShrink: 0 }} />
      </div>
    </div>
  );
}

function QuickActionCard({
  icon: Icon,
  title,
  description,
  onClick,
}: {
  icon: typeof Video;
  title: string;
  description: string;
  onClick: () => void;
}) {
  return (
    <button
      onClick={onClick}
      className="card card-interactive"
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: '12px',
        padding: '16px',
        textAlign: 'left',
        width: '100%',
        border: 'none',
      }}
    >
      <div
        className="icon-container icon-container-sm"
        style={{ background: 'rgba(123, 75, 216, 0.1)' }}
      >
        <Icon size={18} style={{ color: 'var(--accent)' }} />
      </div>
      <div>
        <h4 style={{ fontWeight: '600', color: 'var(--gray-900)', fontSize: '14px' }}>{title}</h4>
        <p style={{ fontSize: '12px', color: 'var(--gray-500)', marginTop: '2px' }}>{description}</p>
      </div>
      <ChevronRight size={16} style={{ color: 'var(--gray-400)', marginLeft: 'auto', flexShrink: 0 }} />
    </button>
  );
}
